/**
 * OnlyJalen API Service
 * Handles all communication with the backend
 * Supports JWT access tokens with automatic refresh
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://onlyjalen-api.jalen1wa.workers.dev';
console.log('[API] Base URL:', API_BASE);

// ==================== TOKEN MANAGEMENT ====================

interface TokenStore {
  accessToken: string | null;
  refreshToken: string | null;
}

const TOKEN_KEYS = {
  ACCESS: 'onlyjalen_access_token',
  REFRESH: 'onlyjalen_refresh_token',
  ACCESS_EXPIRY: 'onlyjalen_access_expiry',
  REFRESH_EXPIRY: 'onlyjalen_refresh_expiry',
};

// Initialize from localStorage
let tokens: TokenStore = {
  accessToken: localStorage.getItem(TOKEN_KEYS.ACCESS),
  refreshToken: localStorage.getItem(TOKEN_KEYS.REFRESH),
};

// Token expiry times (stored separately since tokens aren't JWTs)
let tokenExpiry = {
  access: parseInt(localStorage.getItem(TOKEN_KEYS.ACCESS_EXPIRY) || '0', 10),
  refresh: parseInt(localStorage.getItem(TOKEN_KEYS.REFRESH_EXPIRY) || '0', 10),
};

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export const setTokens = (
  accessToken: string | null,
  refreshToken: string | null,
  accessExpiresAt?: number,
  refreshExpiresAt?: number
) => {
  tokens.accessToken = accessToken;
  tokens.refreshToken = refreshToken;

  if (accessToken) {
    localStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
    // Default to 1 hour if no expiry provided
    const expiry = accessExpiresAt || Date.now() + 3600000;
    tokenExpiry.access = expiry;
    localStorage.setItem(TOKEN_KEYS.ACCESS_EXPIRY, String(expiry));
  } else {
    localStorage.removeItem(TOKEN_KEYS.ACCESS);
    localStorage.removeItem(TOKEN_KEYS.ACCESS_EXPIRY);
    tokenExpiry.access = 0;
  }

  if (refreshToken) {
    localStorage.setItem(TOKEN_KEYS.REFRESH, refreshToken);
    // Default to 7 days if no expiry provided
    const expiry = refreshExpiresAt || Date.now() + 604800000;
    tokenExpiry.refresh = expiry;
    localStorage.setItem(TOKEN_KEYS.REFRESH_EXPIRY, String(expiry));
  } else {
    localStorage.removeItem(TOKEN_KEYS.REFRESH);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_EXPIRY);
    tokenExpiry.refresh = 0;
  }
};

export const clearTokens = () => {
  setTokens(null, null);
};

export const getAccessToken = () => tokens.accessToken;
export const getRefreshToken = () => tokens.refreshToken;

// ==================== JWT UTILITIES ====================

interface JWTPayload {
  sub?: string;
  exp?: number;
  iat?: number;
  is_admin?: boolean;
  isAdmin?: boolean;
  email?: string;
  [key: string]: unknown;
}

/**
 * Decode JWT payload without verification (for client-side use only)
 * Server still validates the signature on every request
 */
export const decodeJWT = (token: string | null): JWTPayload | null => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // Base64url decode
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * Check if token is expired (with 60s buffer for clock skew)
 * Uses stored expiry time since tokens are session tokens, not JWTs
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;

  // First try JWT decode (for backward compatibility)
  const payload = decodeJWT(token);
  if (payload?.exp) {
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now + 60;
  }

  // Fall back to stored expiry time for non-JWT tokens
  const isAccessToken = token === tokens.accessToken;
  const expiry = isAccessToken ? tokenExpiry.access : tokenExpiry.refresh;

  // If no expiry stored, assume valid (legacy tokens)
  if (!expiry) return false;

  // Check with 60s buffer
  return expiry < Date.now() + 60000;
};

/**
 * Get isAdmin from JWT claims (secure - can't be spoofed via localStorage)
 */
export const getIsAdminFromToken = (): boolean => {
  const payload = decodeJWT(tokens.accessToken);
  return payload?.is_admin === true || payload?.isAdmin === true;
};

/**
 * Check if user is authenticated with a valid, non-expired token
 */
export const isAuthenticated = (): boolean => {
  if (!tokens.accessToken) return false;
  return !isTokenExpired(tokens.accessToken);
};

/**
 * Check if token exists (may be expired) - for session restoration
 */
export const hasStoredToken = (): boolean => !!tokens.accessToken;

// Legacy support for old token format
export const setAuthToken = (token: string | null) => {
  // For backwards compatibility, treat single token as access token
  setTokens(token, tokens.refreshToken);
};

export const getAuthToken = () => tokens.accessToken;

// ==================== TOKEN REFRESH ====================

async function refreshAccessToken(): Promise<boolean> {
  if (!tokens.refreshToken) {
    return false;
  }

  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });

      if (!response.ok) {
        // Refresh token invalid/expired - clear tokens
        clearTokens();
        return false;
      }

      const data = await response.json();
      setTokens(data.accessToken, data.refreshToken, data.accessExpiresAt, data.refreshExpiresAt);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearTokens();
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ==================== API REQUEST HELPER ====================

export class APIError extends Error {
  status: number;
  code?: string;
  data?: any; // Include full response data for handling special cases

  constructor(message: string, status: number, code?: string, data?: any) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
  retryOnUnauthorized?: boolean;
}

async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { skipAuth = false, retryOnUnauthorized = true, ...fetchOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers || {}),
  };

  if (!skipAuth && tokens.accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  const fullUrl = `${API_BASE}${endpoint}`;
  console.log(`[API] Fetching: ${fetchOptions.method || 'GET'} ${fullUrl}`);

  let response: Response;
  try {
    response = await fetch(fullUrl, {
      ...fetchOptions,
      headers,
      mode: 'cors',
    });
  } catch (fetchError: any) {
    console.error(`[API] Network error for ${fullUrl}:`, fetchError);
    console.error(`[API] Error details:`, {
      name: fetchError.name,
      message: fetchError.message,
      stack: fetchError.stack,
    });
    // Re-throw with more context
    throw new APIError(
      `Network error: ${fetchError.message || 'Failed to connect to server'}`,
      0,
      'NETWORK_ERROR'
    );
  }

  // Handle 401 - try to refresh token and retry
  if (response.status === 401 && retryOnUnauthorized && tokens.refreshToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry the request with new access token
      return apiRequest<T>(endpoint, { ...options, retryOnUnauthorized: false });
    }
    // Refresh failed - throw auth error
    throw new APIError('Session expired. Please log in again.', 401, 'SESSION_EXPIRED');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new APIError(
      // Routes vary on which field carries the human message:
      //   * Older worker code uses `error`
      //   * The Reddit + a few newer routes use `message`
      //   * Some return validation arrays — we surface the first one
      // Falls through to a generic string only when nothing usable is present.
      data.error
        || data.message
        || (Array.isArray(data.errors) && data.errors[0]?.message)
        || 'API request failed',
      response.status,
      data.code,
      data // Include full response data for handling 402/403 cases
    );
  }

  return data;
}

// ==================== AUTH API ====================

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt?: number;
  refreshExpiresAt?: number;
  user: User;
}

export interface User {
  id: string;
  username: string;
  email: string;
  display_name: string;
  avatar?: string;
  bio?: string;
  is_admin: boolean;
  is_subscribed: boolean;
  subscription_tier?: string;
  subscription_expiry?: string;
  created_at: string;
}

export const authAPI = {
  async register(email: string, password: string, username: string, displayName?: string, selectedTier?: string): Promise<AuthResponse> {
    // Attach UTM attribution captured at landing time. Backend stores
    // it on the users row so we can later attribute paid conversions
    // back to Reddit (and other) campaigns. Unknown fields are safely
    // ignored by older API builds.
    let attribution: any = undefined;
    try {
      // Dynamic import to keep auth flow decoupled from the util in
      // case it's ever tree-shaken / replaced. Promise.resolve so we
      // don't block the request on an import failure.
      const mod = await import('../utils/utm');
      attribution = mod.getStoredUtm() || undefined;
    } catch { /* attribution best-effort */ }

    const data = await apiRequest<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, username, displayName, selectedTier, attribution }),
      skipAuth: true,
    });
    // Only set tokens if user is not pending
    if (data.accessToken && data.user && (data.user as any).status !== 'pending') {
      setTokens(data.accessToken, data.refreshToken, data.accessExpiresAt, data.refreshExpiresAt);
    }
    return data;
  },

  async checkPaymentStatus(email: string): Promise<{ status: 'pending' | 'active'; user?: any }> {
    return apiRequest('/api/auth/check-payment-status', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  },

  async resendPaymentEmail(email: string): Promise<{ success: boolean }> {
    return apiRequest('/api/auth/resend-payment-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    const data = await apiRequest<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      skipAuth: true,
    });
    setTokens(data.accessToken, data.refreshToken, data.accessExpiresAt, data.refreshExpiresAt);
    return data;
  },

  async logout(): Promise<void> {
    try {
      await apiRequest('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
    } finally {
      clearTokens();
    }
  },

  async logoutAllDevices(): Promise<void> {
    await apiRequest('/api/auth/logout-all', { method: 'POST' });
    clearTokens();
  },

  async getCurrentUser(): Promise<{ user: User }> {
    return apiRequest('/api/auth/me');
  },

  async refreshTokens(): Promise<boolean> {
    return refreshAccessToken();
  },
};

// ==================== SUBSCRIPTIONS API ====================

export interface SubscriptionTier {
  id: string;
  name: string;
  price: number; // in cents
  interval: 'month' | 'year' | null;
  intervalCount?: number;
  description: string;
}

export interface Subscription {
  id: string;
  user_id: string;
  creator_id: string;
  paypal_subscription_id?: string;
  plan_type: string;
  price_cents: number;
  status: 'pending' | 'active' | 'cancelled' | 'expired' | 'suspended';
  current_period_start?: string;
  current_period_end?: string;
  created_at: string;
}

export interface CreateSubscriptionResponse {
  subscriptionId: string;
  orderId?: string;
  paypalSubscriptionId?: string;
  approvalUrl: string;
  type: 'order' | 'subscription';
}

export interface ActivateSubscriptionData {
  subscriptionId: string;
  paypalOrderId?: string;
  paypalSubscriptionId?: string;
}

export const subscriptionsAPI = {
  async getTiers(): Promise<{ tiers: SubscriptionTier[] }> {
    return apiRequest('/api/subscriptions/tiers', { skipAuth: true });
  },

  async getMySubscription(): Promise<{ subscription: Subscription | null }> {
    return apiRequest('/api/subscriptions/me');
  },

  async create(payload: {
    planType: string;
    userId?: string;
    email?: string;
    displayName?: string;
    creatorId?: string;
  }): Promise<CreateSubscriptionResponse & { approvalUrl?: string; subscriptionId?: string; amount?: string }> {
    return apiRequest('/api/subscriptions/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async activate(data: ActivateSubscriptionData): Promise<{ success: boolean; subscription: Subscription }> {
    return apiRequest('/api/subscriptions/activate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async cancel(subscriptionId: string, reason?: string): Promise<{ success: boolean; message: string }> {
    return apiRequest('/api/subscriptions/cancel', {
      method: 'POST',
      body: JSON.stringify({ subscriptionId, reason }),
    });
  },

  /**
   * Renew the current user's subscription at the same tier (or an
   * explicit planType). Returns a paypal.me approval URL the caller
   * should open in a new tab. Idempotent — if a pending renewal
   * already exists for the same plan, that one is reused.
   */
  async renew(planType?: string): Promise<{
    success: boolean;
    subscriptionId: string;
    planType: string;
    amount: string;
    approvalUrl: string;
    reused: boolean;
  }> {
    return apiRequest('/api/subscriptions/renew', {
      method: 'POST',
      body: JSON.stringify(planType ? { planType } : {}),
    });
  },

  /**
   * Create a true recurring PayPal subscription against one of the
   * live Billing Plans (monthly/quarterly/yearly). Returns the
   * paypal_subscription_id to hand back to the Smart Buttons
   * createSubscription callback.
   *
   * Auth: prefer to call this while signed in — backend uses the
   * session user_id. As a fallback the call can include {userId,
   * email, displayName} for the just-registered-not-yet-logged-in case.
   */
  async paypalCreate(payload: {
    planType: 'monthly' | 'quarterly' | 'yearly';
    userId?: string;
    email?: string;
    displayName?: string;
  }): Promise<{
    success: boolean;
    subscriptionId: string;
    paypalSubscriptionId: string;
    planType: string;
    planId: string;
    approvalUrl: string;
    status: string;
  }> {
    return apiRequest('/api/subscriptions/paypal-create', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// ==================== NOTIFICATIONS API ====================

export interface Notification {
  id: string;
  user_id: string;
  type: 'post' | 'message' | 'order' | 'product' | 'tip' | 'welcome';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
}

export const notificationsAPI = {
  async getAll(): Promise<{ notifications: Notification[] }> {
    return apiRequest('/api/notifications');
  },

  async markAsRead(notificationId: string): Promise<void> {
    await apiRequest(`/api/notifications/${notificationId}/read`, { method: 'PUT' });
  },

  async markAllAsRead(): Promise<void> {
    await apiRequest('/api/notifications/read-all', { method: 'PUT' });
  },
};

// ==================== POSTS API ====================

export interface Post {
  id: string;
  user_id: string;
  content?: string;
  media_type?: 'image' | 'video' | 'gallery';
  media_url?: string;
  is_locked: boolean;
  price?: number;
  is_story: boolean;
  expires_at?: string;
  likes: number;
  comments: number;
  tips: number;
  created_at: string;
  username?: string;
  display_name?: string;
  avatar?: string;
  isUnlocked?: boolean;
  isLikedByUser?: boolean;
}

export interface CreatePostData {
  content?: string;
  mediaType?: 'image' | 'video' | 'gallery';
  mediaUrl?: string;
  mediaUrls?: string[];
  /** Optional client-extracted (or server-generated) poster image for video. */
  posterUrl?: string;
  isLocked?: boolean;
  price?: number;
  isStory?: boolean;
  expiresAt?: string;
}

export const postsAPI = {
  async getAll(limit = 20, offset = 0): Promise<{ posts: Post[] }> {
    return apiRequest(`/api/posts?limit=${limit}&offset=${offset}`);
  },

  async getStories(): Promise<{ posts: Post[] }> {
    return apiRequest('/api/posts?story=true');
  },

  async create(post: CreatePostData): Promise<{ postId: string }> {
    return apiRequest('/api/posts', {
      method: 'POST',
      body: JSON.stringify(post),
    });
  },

  async update(postId: string, updates: Partial<CreatePostData>): Promise<{ postId: string }> {
    return apiRequest(`/api/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async delete(postId: string): Promise<{ success: boolean }> {
    return apiRequest(`/api/posts/${postId}`, { method: 'DELETE' });
  },

  async like(postId: string): Promise<{ liked: boolean }> {
    return apiRequest(`/api/posts/${postId}/like`, { method: 'POST' });
  },

  /** Record a story view. Backend dedupes by (story, viewer) — calling
   *  this repeatedly only bumps the per-viewer counter, doesn't pad the
   *  distinct-viewers count. Admin/creator views are ignored server-side. */
  async recordView(postId: string): Promise<{ success: boolean; totalViewers?: number }> {
    return apiRequest(`/api/posts/${postId}/view`, { method: 'POST' });
  },

  /** Admin-only — list everyone who viewed a story. */
  async getViews(postId: string): Promise<{
    success: boolean;
    storyId: string;
    totalViewers: number;
    viewers: Array<{
      viewer_user_id: string;
      viewed_at: string;
      last_viewed_at: string;
      view_count: number;
      display_name: string;
      username: string;
      avatar: string;
      email: string;
    }>;
  }> {
    return apiRequest(`/api/posts/${postId}/views`);
  },

  async unlock(postId: string, paypalOrderId: string): Promise<{ success: boolean; unlocked: boolean }> {
    return apiRequest(`/api/posts/${postId}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ paypalOrderId }),
    });
  },
};

// ==================== MESSAGES API ====================

export interface Reaction {
  emoji: string;
  count: number;
  users: string[];
  includesMe: boolean;
}

export interface Message {
  id: string;
  conversation_id?: string;
  from_user_id: string;
  to_user_id: string;
  content: string;
  read: boolean;
  price?: number;
  created_at: string;
  updated_at?: string;
  from_name?: string;
  from_avatar?: string;
  to_name?: string;
  to_avatar?: string;
  sender_name?: string;
  sender_avatar?: string;
  status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  attachments?: string[];
  reactions?: Reaction[];
  edited?: boolean;
}

export interface Conversation {
  id: string;
  partner_id: string;
  partner_name: string;
  partner_avatar: string;
  partner_email?: string;
  last_message: string;
  last_message_at: string;
  unread_count: number;
  is_online?: boolean;
}

export const messagesAPI = {
  // Get all conversations
  async getConversations(): Promise<{ conversations: Conversation[] }> {
    return apiRequest('/api/messages/conversations');
  },

  // Get a single conversation by ID
  async getConversation(id: string): Promise<{ conversation: Conversation; messages: Message[] }> {
    return apiRequest(`/api/messages/conversations/${id}`);
  },

  // Find or create a direct message conversation
  async findOrCreateDM(userId: string): Promise<{ conversationId: string; id: string; created: boolean }> {
    return apiRequest('/api/messages/conversations/dm', {
      method: 'POST',
      body: JSON.stringify({ subscriber_id: userId, user_id: userId }),
    });
  },

  // Get messages for a conversation (cursor-based pagination)
  async getMessages(convId: string, before?: string): Promise<{ messages: Message[]; hasMore: boolean }> {
    const url = before
      ? `/api/messages/conversations/${convId}/messages?before=${before}&limit=50`
      : `/api/messages/conversations/${convId}/messages?limit=50`;
    return apiRequest(url);
  },

  // Send a message to a conversation
  async sendMessage(
    convId: string,
    content: string,
    attachments?: string[]
  ): Promise<{ id: string; messageId: string; message: Message }> {
    return apiRequest(`/api/messages/conversations/${convId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, attachments }),
    });
  },

  // Edit a message
  async editMessage(messageId: string, content: string): Promise<{ success: boolean; message: Message }> {
    return apiRequest(`/api/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
  },

  // Delete a message
  async deleteMessage(messageId: string): Promise<{ success: boolean }> {
    return apiRequest(`/api/messages/${messageId}`, { method: 'DELETE' });
  },

  // Mark a conversation as read
  async markAsRead(convId: string): Promise<{ success: boolean }> {
    return apiRequest(`/api/messages/conversations/${convId}/read`, { method: 'POST' });
  },

  // Get total unread message count
  async getUnreadCount(): Promise<{ count: number }> {
    return apiRequest('/api/messages/unread-count');
  },

  // Toggle a reaction on a message
  async toggleReaction(messageId: string, emoji: string): Promise<{ success: boolean; reactions: Reaction[] }> {
    return apiRequest(`/api/messages/${messageId}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    });
  },

  // Legacy: Get all messages (deprecated, use getConversations instead)
  async getAll(): Promise<{ messages: Message[] }> {
    return apiRequest('/api/messages');
  },

  // Legacy: Send message by user ID (deprecated, use sendMessage with conversation ID)
  async send(toUserId: string, content: string, price?: number): Promise<{ messageId: string }> {
    return apiRequest('/api/messages', {
      method: 'POST',
      body: JSON.stringify({ toUserId, content, price }),
    });
  },
};

// ==================== PRODUCTS API ====================

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  sale_price?: number;
  category?: string;
  stock: number;
  featured: boolean;
  sold: number;
  rating: number;
  reviews: number;
  images: string[];
  sizes: { size: string; stock: number }[];
  created_at: string;
}

export interface CreateProductData {
  name: string;
  description?: string;
  price: number;
  originalPrice?: number;
  category?: string;
  imageUrl?: string;
  images?: string[];
  featured?: boolean;
  stock?: number;
}

export const productsAPI = {
  async getAll(options?: { category?: string; featured?: boolean }): Promise<{ products: Product[] }> {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.featured) params.set('featured', 'true');
    const query = params.toString();
    return apiRequest(`/api/products${query ? `?${query}` : ''}`);
  },

  async create(product: CreateProductData): Promise<{ success: boolean; productId: string; message: string }> {
    return apiRequest('/api/products', {
      method: 'POST',
      body: JSON.stringify(product),
    });
  },

  async update(productId: string, updates: Partial<CreateProductData>): Promise<{ success: boolean; message: string }> {
    return apiRequest(`/api/products/${productId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async remove(productId: string): Promise<{ success: boolean; message: string }> {
    return apiRequest(`/api/products/${productId}`, { method: 'DELETE' });
  },
};

// ==================== ADMIN PUSH EMAIL ====================

export const adminEmailAPI = {
  /**
   * Send a one-off branded push email to a subscriber (or batch).
   * Backend is admin-gated.
   *
   * recipientUserId can be a specific user_id, or the special tokens
   * 'all-active' / 'all-expired' to broadcast (capped at 200 per call).
   */
  async push(payload: {
    recipientUserId: string;
    subject: string;
    message: string;
    imageUrl?: string;
    videoUrl?: string;
    videoPoster?: string;
    includeRenewButton?: boolean;
    renewAmount?: string;
    renewTierLabel?: string;
  }): Promise<{ success: boolean; sent: number; failed: number; total: number }> {
    return apiRequest('/api/admin/push-email', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

// ==================== AI API (admin-only) ====================
//
// Powered by Workers AI (Llama 3.1 on Cloudflare's GPUs). Used by:
//   - PushEmailModal:  auto-draft subject + body when opened
//   - MessagesPage:    "✨ AI Reply" button on a thread

export type PushEmailTone = 'friendly' | 'bold' | 'urgent' | 'personal' | 'flirty';

export const aiAPI = {
  /**
   * Generate a subject + message body for an outbound push email.
   * Subscriber context (status, days since last seen, etc) is loaded
   * server-side from D1 using recipientUserId.
   */
  async draftPushEmail(payload: {
    recipientUserId: string;
    tone?: PushEmailTone;
    customAngle?: string;
    includeRenewCTA?: boolean;
    renewTierLabel?: string;
    renewAmount?: string;
  }): Promise<{
    subject: string;
    message: string;
    recipient: { id: string; displayName: string; status: string };
    /** v2: model's read of the subscriber. Surfaced to the admin so they
     *  understand WHY the drafts look the way they do. */
    read?: { vibe: string; rapport: string; angle: string };
    /** v2: 3 distinct draft variants. UI shows a picker; selection swaps
     *  subject + message. variants[0] always matches the top-level
     *  subject/message for back-compat with older clients. */
    variants?: Array<{ label: string; subject: string; preview: string; body: string }>;
  }> {
    return apiRequest('/api/admin/ai/draft-push-email', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Generate a one-shot DM reply for the admin, based on the last 25
   * messages in the conversation. Optional `intent` lets the admin
   * steer the response ("be flirty", "decline politely", etc).
   * Optional `tone` overrides the default flirty-provocative voice.
   */
  async draftProductDescription(payload: {
    productName: string;
    category?: string;
    price?: number;
    notes?: string;
    tone?: 'premium' | 'playful' | 'urgent' | 'minimal';
  }): Promise<{ description: string }> {
    return apiRequest('/api/admin/ai/draft-product-description', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async draftReply(payload: {
    conversationId: string;
    intent?: string;
    tone?: PushEmailTone;
    /** v2: short / medium / long output. Drives both prompt directive
     *  and token cap server-side. Defaults to 'medium' on the server. */
    length?: 'short' | 'medium' | 'long';
  }): Promise<{ reply: string; partnerName: string }> {
    return apiRequest('/api/admin/ai/draft-reply', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Vision-classify a freshly uploaded product image.
   * Backend pipes the image through Workers AI's llava model and
   * returns suggested {name, description, category} so the admin can
   * one-tap-fill the AddProductModal. URL must reference an asset we
   * host (SSRF guard).
   */
  async classifyProductImage(imageUrl: string): Promise<{ suggestion: { name?: string; description?: string; category?: string }; raw: string }> {
    return apiRequest('/api/admin/ai/classify-product-image', {
      method: 'POST',
      body: JSON.stringify({ imageUrl }),
    });
  },
};

// ==================== ORDERS API ====================

export type OrderStatus = 'pending' | 'paid' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';

export interface Order {
  id: string;
  user_id: string;
  subtotal: number;
  shipping: number;
  tax: number;
  total: number;
  subtotal_cents?: number;
  shipping_cents?: number;
  tax_cents?: number;
  total_cents?: number;
  status: OrderStatus;
  shipping_address: string | null;
  shipping_address_json?: string | null;
  paypal_order_id?: string | null;
  paypal_capture_id?: string | null;
  tracking_number?: string | null;
  created_at: string;
  updated_at?: string;
  items: OrderItem[];
}

export interface OrderItem {
  id?: string;
  product_id: string;
  product_name: string;
  product_image?: string | null;
  size: string | null;
  quantity: number;
  price: number;
  unit_price_cents?: number;
}

export interface CreateOrderItem {
  productId: string;
  size?: string;
  quantity: number;
}

export const ordersAPI = {
  async getAll(): Promise<{ orders: Order[] }> {
    return apiRequest('/api/orders');
  },

  async getById(orderId: string): Promise<Order> {
    return apiRequest(`/api/orders/${orderId}`);
  },

  async create(items: CreateOrderItem[], shippingAddressId: string): Promise<{
    success: boolean;
    orderId: string;
    paypalOrderId: string;
    approvalUrl: string;
  }> {
    return apiRequest('/api/orders', {
      method: 'POST',
      body: JSON.stringify({ items, shippingAddressId }),
    });
  },

  async capture(orderId: string, paypalOrderId: string, paypalCaptureId?: string): Promise<{ success: boolean; status: string }> {
    return apiRequest('/api/orders/capture', {
      method: 'POST',
      body: JSON.stringify({ orderId, paypalOrderId, paypalCaptureId }),
    });
  },

  async updateStatus(orderId: string, status: string, trackingNumber?: string): Promise<{ success: boolean; status: string }> {
    return apiRequest(`/api/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, trackingNumber }),
    });
  },
};

// ==================== PROFILE API ====================

export interface ShippingAddress {
  id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  is_default: boolean;
}

export interface CreateAddressData {
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  isDefault?: boolean;
}

export const profileAPI = {
  async get(): Promise<{ user: any }> {
    return apiRequest('/api/profile');
  },

  async update(updates: { display_name?: string; bio?: string; avatar?: string }): Promise<void> {
    await apiRequest('/api/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async updateAvatar(avatarUrl: string): Promise<void> {
    await apiRequest('/api/profile/avatar', {
      method: 'PUT',
      body: JSON.stringify({ avatarUrl }),
    });
  },

  async getAddresses(): Promise<{ addresses: ShippingAddress[] }> {
    return apiRequest('/api/addresses');
  },

  async addAddress(address: CreateAddressData): Promise<{ addressId: string }> {
    return apiRequest('/api/addresses', {
      method: 'POST',
      body: JSON.stringify(address),
    });
  },
};

// ==================== MEDIA API ====================

export interface UploadResponse {
  success: boolean;
  key: string;
  url: string;
  contentType: string;
  size: number;
}

export interface MediaFile {
  key: string;
  size: number;
  uploaded: string;
  url: string;
}

export interface MediaListResponse {
  files: MediaFile[];
  cursor: string | null;
  hasMore: boolean;
}

export const mediaAPI = {
  // Upload file using FormData (recommended for large files)
  async uploadFile(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/api/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${tokens.accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const data = await response.json();
      throw new APIError(data.error || 'Upload failed', response.status);
    }

    return response.json();
  },

  // Upload file using base64 (for smaller files)
  async uploadBase64(data: string, filename: string, mimeType: string): Promise<UploadResponse> {
    return apiRequest('/api/upload', {
      method: 'POST',
      body: JSON.stringify({ data, filename, mimeType }),
    });
  },

  // Get media URL (handles auth for private content)
  async getMediaUrl(key: string): Promise<string> {
    // Use API endpoint for media
    return `${API_BASE}/api/media/${key}`;
  },

  // Convert old media.onlyjalen.com URLs to API URLs
  fixMediaUrl(url: string): string {
    if (url && url.includes('media.onlyjalen.com')) {
      const key = url.replace('https://media.onlyjalen.com/', '');
      return `${API_BASE}/api/media/${key}`;
    }
    return url;
  },

  // List uploaded media (admin only)
  async list(cursor?: string, limit = 50): Promise<MediaListResponse> {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    params.set('limit', limit.toString());
    return apiRequest(`/api/media?${params.toString()}`);
  },

  // Delete media (admin only)
  async delete(key: string): Promise<{ success: boolean; deleted: string }> {
    return apiRequest(`/api/media/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    });
  },
};

// ==================== TIPS API ====================

export interface TipResponse {
  tipId: string;
  paypalOrderId: string;
  approvalUrl: string;
  amount: number;
}

export const tipsAPI = {
  async send(amount: number, creatorId: string, message?: string): Promise<TipResponse> {
    return apiRequest('/api/tips', {
      method: 'POST',
      body: JSON.stringify({ amount, creatorId, message }),
    });
  },

  async capture(tipId: string, paypalOrderId: string): Promise<{ success: boolean }> {
    return apiRequest('/api/tips/capture', {
      method: 'POST',
      body: JSON.stringify({ tipId, paypalOrderId }),
    });
  },
};

// ==================== ADMIN API ====================

export interface EarningsSummary {
  total_gross: number;
  total_fees: number;
  total_net: number;
  transaction_count: number;
}

export interface CreatorEarning {
  id: string;
  creator_id: string;
  transaction_id: string;
  gross_amount_cents: number;
  platform_fee_cents: number;
  net_amount_cents: number;
  source_type: string;
  source_id: string;
  status: string;
  created_at: string;
}

// ==================== COMMENTS API ====================

export interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  username: string;
  display_name: string;
  avatar: string;
  created_at: string;
}

export const commentsAPI = {
  async getForPost(postId: string): Promise<{ comments: Comment[] }> {
    return apiRequest(`/api/posts/${postId}/comments`, { skipAuth: true });
  },

  async create(postId: string, content: string): Promise<{ success: boolean; commentId: string; comment: Comment }> {
    return apiRequest(`/api/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },
};

// ==================== OFFERS API ====================

export interface ProductOffer {
  id: string;
  product_id: string;
  user_id: string;
  amount: number;
  message?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'countered' | 'expired';
  counter_amount?: number;
  expires_at: string;
  created_at: string;
  username?: string;
  display_name?: string;
  avatar?: string;
  product_name?: string;
  product_image?: string;
  product_price?: number;
}

export const offersAPI = {
  async getForProduct(productId: string): Promise<{ offers: ProductOffer[] }> {
    return apiRequest(`/api/products/${productId}/offers`, { skipAuth: true });
  },

  async create(productId: string, amount: number, message?: string): Promise<{ success: boolean; offerId: string; expiresAt: string }> {
    return apiRequest(`/api/products/${productId}/offers`, {
      method: 'POST',
      body: JSON.stringify({ amount, message }),
    });
  },

  async getPending(): Promise<{ offers: ProductOffer[] }> {
    return apiRequest('/api/offers/pending');
  },

  async getMine(): Promise<{ offers: ProductOffer[] }> {
    return apiRequest('/api/offers/mine');
  },

  async accept(offerId: string): Promise<{ success: boolean; status: string }> {
    return apiRequest(`/api/offers/${offerId}/accept`, { method: 'POST' });
  },

  async decline(offerId: string): Promise<{ success: boolean; status: string }> {
    return apiRequest(`/api/offers/${offerId}/decline`, { method: 'POST' });
  },

  async counter(offerId: string, counterAmount: number): Promise<{ success: boolean; status: string; counterAmount: number }> {
    return apiRequest(`/api/offers/${offerId}/counter`, {
      method: 'POST',
      body: JSON.stringify({ counterAmount }),
    });
  },

  async confirmPayment(offerId: string, notes?: string): Promise<{ success: boolean; message: string }> {
    return apiRequest(`/api/offers/${offerId}/confirm-payment`, {
      method: 'POST',
      body: JSON.stringify({ confirmedBy: 'admin', notes }),
    });
  },

  async getAll(status?: string): Promise<{ offers: ProductOffer[] }> {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/api/offers${params}`);
  },
};

// ==================== ADMIN API ====================

export interface AttributionSnapshot {
  windowDays: number;
  totalSignups: number;
  bySource: Array<{ source: string; count: number }>;
  recent: Array<{
    id: string;
    displayName: string;
    email: string;
    source: string;
    attribution: { source?: string; medium?: string; campaign?: string; landingPath?: string; capturedAt?: number } | null;
    createdAt: string;
  }>;
}

export const adminAPI = {
  async getEarnings(): Promise<{
    summary: EarningsSummary;
    recentEarnings: CreatorEarning[];
    pendingPayoutCents: number;
  }> {
    return apiRequest('/api/admin/earnings');
  },

  /**
   * Where did recent signups come from? Powers the dashboard
   * attribution card. `days` defaults to 30, clamped server-side
   * between 1 and 365.
   */
  async getAttribution(days = 30): Promise<AttributionSnapshot> {
    return apiRequest(`/api/admin/attribution?days=${days}`);
  },
};

// ==================== HEALTH API ====================

export const healthAPI = {
  async check(): Promise<{ status: string; environment: string; timestamp: string }> {
    return apiRequest('/api/health', { skipAuth: true });
  },
};

// ==================== UNLOCK DROPS API ====================

export interface UnlockDropMedia {
  type: 'image' | 'video';
  url: string;
}

export interface CreateUnlockDropInput {
  price: number;
  title: string;
  description?: string;
  body?: string;
  media?: UnlockDropMedia[];
}

export interface CreateUnlockDropResponse {
  success: boolean;
  url: string;
  slug: string;
  invoice: string;
  amount: string;
  error?: string;
}

export const unlockAPI = {
  // Admin-only — creates a pay-to-unlock page on jalenward.com and
  // returns the shareable URL. The owner secret lives in the Worker env,
  // never in the browser bundle. Paste the URL into the chat composer
  // to send it as a paywall card to a subscriber.
  async createDrop(input: CreateUnlockDropInput): Promise<CreateUnlockDropResponse> {
    return apiRequest<CreateUnlockDropResponse>('/api/admin/unlock/create', {
      method: 'POST',
      body: JSON.stringify({
        price: input.price,
        title: input.title,
        description: input.description ?? '',
        content: {
          body: input.body ?? '',
          media: input.media ?? [],
        },
      }),
    });
  },
};

// ==================== DEFAULT EXPORT ====================

// Named export for convenience
export const api = {
  auth: authAPI,
  subscriptions: subscriptionsAPI,
  notifications: notificationsAPI,
  posts: postsAPI,
  messages: messagesAPI,
  products: productsAPI,
  orders: ordersAPI,
  profile: profileAPI,
  media: mediaAPI,
  tips: tipsAPI,
  comments: commentsAPI,
  offers: offersAPI,
  admin: adminAPI,
  health: healthAPI,
  unlock: unlockAPI,
  // Direct method shortcuts
  post: async <T>(endpoint: string, data: any): Promise<T> => {
    return apiRequest<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  get: async <T>(endpoint: string): Promise<T> => {
    return apiRequest<T>(endpoint);
  },
};

// ─────────────────────────────────────────────────────────────────────
// ReddAPI (admin-only) — Reddit posting / search / reply / join via the
// RapidAPI ReddAPI proxy. All requests go through the worker so the
// RapidAPI key and per-account Reddit creds never touch the browser.
// ─────────────────────────────────────────────────────────────────────

export interface RedditAccountSummary {
  id: string;
  username: string;
  label: string | null;
  has_bearer: number | boolean;
  has_cookies: number | boolean;
  has_proxy: number | boolean;
  created_at: string;
  updated_at: string;
}

export interface RedditConfig {
  rapidapi_key_set: boolean;
  cred_secret_set: boolean;
}

export interface RedditSearchHit {
  id: string;
  title: string;
  subreddit: string;
  author: string;
  url: string;
  score: number;
  num_comments: number;
  created_utc: number;
}

export interface RedditActionLogEntry {
  id: string;
  account_id: string | null;
  admin_user_id: string;
  action_type: 'post_profile' | 'post_subreddit' | 'reply' | 'join' | 'search';
  target: string;
  success: number;
  result_summary: string;
  reddapi_status: number | null;
  created_at: string;
}

export const redditAPI = {
  config: () => apiRequest<{ success: boolean } & RedditConfig>('/api/admin/reddit/config'),

  listAccounts: () =>
    apiRequest<{ success: boolean; accounts: RedditAccountSummary[] }>('/api/admin/reddit/accounts'),

  createAccount: (body: {
    username: string;
    label?: string;
    bearer_token?: string;
    cookies?: Record<string, string> | string;
    proxy?: string;
  }) =>
    apiRequest<{ success: boolean; account: RedditAccountSummary; message?: string }>(
      '/api/admin/reddit/accounts',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  deleteAccount: (id: string) =>
    apiRequest<{ success: boolean; message?: string }>(
      `/api/admin/reddit/accounts/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  searchSubreddit: (body: {
    subreddit: string;
    query: string;
    limit?: number;
    sort?: 'new' | 'relevance' | 'hot' | 'top' | 'comments';
    time_filter?: 'hour' | 'day' | 'week' | 'month' | 'year' | 'all';
  }) =>
    apiRequest<{ success: boolean; data?: RedditSearchHit[]; message?: string }>(
      '/api/admin/reddit/search-subreddit',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  postToProfile: (body: {
    account_id: string;
    title: string;
    text?: string;
    image_url?: string;
    video_url?: string;
    is_nsfw?: boolean;
  }) =>
    apiRequest<{ success: boolean; data?: { url?: string; id?: string }; message?: string }>(
      '/api/admin/reddit/post-to-profile',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  postToSubreddit: (body: {
    account_id: string;
    subreddit: string;
    title: string;
    text?: string;
    image_url?: string;
    video_url?: string;
    is_nsfw?: boolean;
    is_spoiler?: boolean;
    flair_id?: string;
    flair_text?: string;
  }) =>
    apiRequest<{ success: boolean; data?: { url?: string; id?: string }; message?: string }>(
      '/api/admin/reddit/post-to-subreddit',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  replyComment: (body: { account_id: string; comment_url: string; reply: string }) =>
    apiRequest<{ success: boolean; data?: { reply_url?: string }; message?: string }>(
      '/api/admin/reddit/reply-comment',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  joinSubreddit: (body: { account_id: string; subreddit: string }) =>
    apiRequest<{ success: boolean; data?: { subscribed?: boolean }; message?: string }>(
      '/api/admin/reddit/join-subreddit',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  log: (limit = 50) =>
    apiRequest<{ success: boolean; log: RedditActionLogEntry[] }>(
      `/api/admin/reddit/log?limit=${limit}`,
    ),
};

export default api;
